import { sanitizeVariableName } from "../utils";
import type { MatchingResult } from "../types";

/**
 * Common field name aliases for fuzzy matching.
 * Keys are canonical names, values are alternative names that should match.
 */
const FIELD_ALIASES: Record<string, string[]> = {
  first_name: ["first", "firstname", "fname"],
  last_name: ["last", "lastname", "lname"],
  full_name: ["fullname", "name"],
  address_1: ["address", "address1", "street"],
  zip: ["zipcode", "zip_code", "postal_code", "postal"],
  state: ["st", "province"],
  city: ["town"],
  coupon_1: ["coupon1"],
  promo_code: ["promocode", "promo"],
  coupon_2: ["coupon2"],
  company: ["organization", "org"],
};

/**
 * Matches canvas variables against file column names.
 * Returns matched and unmatched variable names.
 *
 * @param canvasVariables - Variable names currently on the canvas (unsanitized display names)
 * @param fileColumns - Column names from the selected/uploaded variable file (after mapping)
 */
export function matchVariables(canvasVariables: string[], fileColumns: string[]): MatchingResult {
  const matched: string[] = [];
  const unmatched: string[] = [];

  // Sanitize file columns for comparison
  const sanitizedFileColumns = fileColumns.map((col) => sanitizeVariableName(col));

  // Build reverse alias map: alternative_name -> canonical_name
  const aliasToCanonical: Record<string, string> = {};
  for (const [canonical, aliases] of Object.entries(FIELD_ALIASES)) {
    aliasToCanonical[canonical] = canonical;
    for (const alias of aliases) {
      aliasToCanonical[alias] = canonical;
    }
  }

  fileColumns.forEach((col) => {
    const sanitizedCol = sanitizeVariableName(col);
    const isMatched = canvasVariables.some((canvasVar) => {
      const sanitizedCanvas = sanitizeVariableName(canvasVar);

      // Direct match
      if (sanitizedCanvas === sanitizedCol) return true;

      // Alias match: check if both resolve to the same canonical name
      const canonicalCanvas = aliasToCanonical[sanitizedCanvas];
      const canonicalCol = aliasToCanonical[sanitizedCol];
      if (canonicalCanvas && canonicalCol && canonicalCanvas === canonicalCol) return true;

      return false;
    });

    if (isMatched) {
      matched.push(col);
    } else {
      unmatched.push(col);
    }
  });

  return { matched, unmatched };
}
